import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

const STATUS = {
  success: { dot: 'bg-emerald-500', text: 'text-emerald-700', label: 'success' },
  running: { dot: 'bg-amber-500 animate-pulse', text: 'text-amber-700', label: 'running' },
  error: { dot: 'bg-rose-500', text: 'text-rose-700', label: 'error' },
  idle: { dot: 'bg-zinc-300', text: 'text-muted-foreground', label: 'idle' },
};

export function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.idle;
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-[12px] font-medium', s.text)}>
      {status === 'running'
        ? <Loader2 className="h-3 w-3 animate-spin" />
        : <span className={cn('h-1.5 w-1.5 rounded-full', s.dot)} />}
      {s.label}
    </span>
  );
}

export function QualityBar({ score, className }) {
  if (score == null) return <span className="text-xs text-muted-foreground">—</span>;
  const tone = score >= 95 ? 'bg-emerald-500' : score >= 80 ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
        <div className={cn('h-full rounded-full', tone)} style={{ width: `${Math.max(2, score)}%` }} />
      </div>
      <span className="tnum text-[12px] font-medium text-foreground">{score}%</span>
    </div>
  );
}

// Back-compat alias used in a few places.
export function QualityBadge({ score }) {
  return <QualityBar score={score} />;
}

const FILE_ICON = { csv: '▦', tsv: '▦', xml: '⟨⟩' };

// Make the output file type unmistakable (CSV/TSV highlighted vs XML).
export function FormatBadge({ format, className }) {
  const f = (format || 'xml').toLowerCase();
  const isDelimited = f === 'csv' || f === 'tsv';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase',
        isDelimited ? 'border-primary/40 bg-primary/15 text-foreground' : 'border-border bg-muted text-muted-foreground',
        className
      )}
      title={isDelimited ? `${f.toUpperCase()} (delimited text file)` : 'XML feed'}
    >
      <span aria-hidden>{FILE_ICON[f] || '·'}</span>.{f}
    </span>
  );
}

export function fmtDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function fmtNum(n) {
  return (n ?? 0).toLocaleString();
}

// Compact relative timestamp: just now · 12m ago · 3h ago · yesterday · 4d ago
export function timeAgo(value) {
  if (!value) return '';
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'yesterday' : `${d}d ago`;
}

// Compact elapsed time: 1.6s · 24s · 1m 36s · 1h 2m
export function fmtDur(s) {
  if (s == null) return '';
  if (s < 10) return `${s.toFixed(1)}s`;
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  return `${Math.floor(s / 3600)}h ${Math.round((s % 3600) / 60)}m`;
}
